import React from 'react';
import { Button, Avatar, Badge } from '@/components/ui';
import { roleConfigMap as ROLE_MAP } from '@/utils/role';
import type { CompanyMember } from '../types/company.types';

interface RoleAssignDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  member: CompanyMember | null;
  onAssign: (roleCode: string) => Promise<void>;
}

export function RoleAssignDrawer({ isOpen, onClose, member, onAssign }: RoleAssignDrawerProps) {
  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />

      <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-md transform transition-transform animate-in slide-in-from-right duration-500">
          <div className="flex h-full flex-col bg-white shadow-2xl">
            <div className="px-6 py-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Phân quyền thành viên</h2>
                <p className="text-xs text-slate-500 mt-1">Thay đổi vai trò của {member.full_name} trong hệ thống.</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full text-slate-400">
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              <div className="flex flex-col items-center p-6 bg-slate-50 rounded-3xl border border-slate-100">
                <Avatar src={member.avatar_path} name={member.full_name} size="lg" className="mb-3" />
                <h3 className="font-bold text-slate-900">{member.full_name}</h3>
                <p className="text-xs text-slate-500 mb-2">{member.email}</p>
                <Badge variant="neutral" className="bg-white border-slate-200">
                  Hiện tại: {ROLE_MAP[member.role_code]?.label}
                </Badge>
              </div>

              <div className="space-y-3">
                <h4 className="text-[10px] font-bold text-slate-400  tracking-widest px-1">Chọn vai trò mới</h4>
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(ROLE_MAP)
                    .filter(([code]) => code !== 'super_admin')
                    .map(([code, config]) => (
                      <button
                        key={code}
                        onClick={() => onAssign(code)}
                        className={`
                          p-4 rounded-2xl border text-left transition-all
                          ${member.role_code === code
                            ? 'border-os-primary bg-os-primary/5 ring-1 ring-os-primary'
                            : 'border-slate-100 hover:border-os-primary/30 hover:bg-slate-50'}
                        `}
                      >
                        <div className="flex justify-between items-start">
                          <span className={`font-bold text-sm ${member.role_code === code ? 'text-os-primary' : 'text-slate-900'}`}>
                            {(config as any).label}
                          </span>
                          {member.role_code === code && <i className="ri-checkbox-circle-fill text-os-primary" />}
                        </div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                          Cho phép truy cập các module thi công, bảo trì và báo cáo ở cấp độ {(config as any).label.toLowerCase()}.
                        </p>
                      </button>
                    ))
                  }
                </div>
              </div>
            </div>

            <div className="px-6 py-6 border-t border-slate-100 bg-slate-50">
              <Button variant="outline" className="w-full" onClick={onClose}>
                Đóng
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
